import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserPlus, DotsThreeVertical, Trash, Crown, ShieldCheck, User as UserIcon, Clock, Desktop } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_META = {
  owner: { label: "Owner", icon: Crown, cls: "text-amber-400" },
  admin: { label: "Admin", icon: ShieldCheck, cls: "text-blue-400" },
  member: { label: "Member", icon: UserIcon, cls: "text-zinc-400" },
};

export default function Team() {
  const { currentOrg, currentOrgId, refreshOrgs } = useAuth();
  const [data, setData] = useState({ members: [], invites: [], my_role: "member" });
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [resending, setResending] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignIds, setAssignIds] = useState([]);
  const [removeTarget, setRemoveTarget] = useState(null);

  const isAdmin = data.my_role === "owner" || data.my_role === "admin";
  const memberLimit = currentOrg?.limits?.max_members;

  const load = useCallback(async () => {
    if (!currentOrgId) return;
    try {
      const [m, mach] = await Promise.all([
        api.get(`/orgs/${currentOrgId}/members`),
        api.get(`/orgs/${currentOrgId}/machines`),
      ]);
      setData(m.data);
      setMachines(mach.data);
    } catch {
      toast.error("Could not load team");
    } finally {
      setLoading(false);
    }
  }, [currentOrgId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const totalSeats = data.members.length + data.invites.length;

  const sendInvite = async () => {
    if (!email.trim()) return;
    try {
      await api.post(`/orgs/${currentOrgId}/invites`, { email: email.trim(), role });
      toast.success("Invitation sent");
      setInviteOpen(false); setEmail(""); setRole("member");
      load(); refreshOrgs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invite");
    }
  };

  const changeRole = async (m, newRole) => {
    try {
      await api.patch(`/orgs/${currentOrgId}/members/${m.user_id}`, { role: newRole });
      toast.success(`${m.name} is now ${newRole}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const confirmRemove = async () => {
    const m = removeTarget; setRemoveTarget(null);
    try {
      await api.delete(`/orgs/${currentOrgId}/members/${m.user_id}`);
      toast.success(`Removed ${m.name}`);
      load(); refreshOrgs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const revokeInvite = async (inv) => {
    try {
      await api.delete(`/orgs/${currentOrgId}/invites/${inv.invite_id}`);
      load(); refreshOrgs();
    } catch { toast.error("Failed"); }
  };

  const resendInvite = async (inv) => {
    setResending(inv.invite_id);
    try {
      await api.post(`/orgs/${currentOrgId}/invites/${inv.invite_id}/resend`);
      toast.success(`Invite re-sent to ${inv.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend invite");
    } finally {
      setResending(null);
    }
  };

  const openAssign = (m) => { setAssignTarget(m); setAssignIds(m.machine_ids || []); };
  const saveAssign = async () => {
    try {
      await api.put(`/orgs/${currentOrgId}/members/${assignTarget.user_id}/machines`, { machine_ids: assignIds });
      toast.success("Access updated");
      setAssignTarget(null); load();
    } catch { toast.error("Failed to update access"); }
  };

  return (
    <AppShell>
      <div className="px-6 py-8 max-w-5xl w-full mx-auto">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Team</h1>
            <p className="text-sm text-zinc-400 mt-1">
              {totalSeats}{memberLimit != null ? ` / ${memberLimit}` : ""} seats used
              {currentOrg && <span className="text-zinc-600"> · {currentOrg.name}</span>}
            </p>
          </div>
          {isAdmin && (
            <Button data-testid="invite-member-button" onClick={() => setInviteOpen(true)} className="bg-blue-600 hover:bg-blue-500">
              <UserPlus size={18} weight="bold" className="mr-1.5" /> Invite member
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-zinc-500 font-mono text-sm animate-pulse">Loading team…</div>
        ) : (
          <div className="border border-white/10 rounded-lg overflow-hidden">
            {data.members.map((m) => {
              const RM = ROLE_META[m.role] || ROLE_META.member;
              const Icon = RM.icon;
              return (
                <div key={m.user_id} data-testid={`member-row-${m.user_id}`} className="flex items-center justify-between px-5 py-4 border-b border-white/10 last:border-0 hover:bg-zinc-900/50 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar className="w-9 h-9">
                      <AvatarImage src={m.picture} />
                      <AvatarFallback className="bg-zinc-800 text-xs">{m.name?.[0] || m.email?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{m.name || m.email}</div>
                      <div className="font-mono text-xs text-zinc-500 truncate">{m.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {m.role === "member" && (
                      <span className="text-xs text-zinc-500 hidden sm:flex items-center gap-1.5">
                        <Desktop size={13} /> {m.machine_ids?.length || 0} machines
                      </span>
                    )}
                    <span className={`flex items-center gap-1.5 text-xs font-medium ${RM.cls}`}>
                      <Icon size={14} weight="fill" /> {RM.label}
                    </span>
                    {isAdmin && m.role !== "owner" && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`member-menu-${m.user_id}`} className="text-zinc-500 hover:text-white p-1 rounded transition-colors">
                            <DotsThreeVertical size={20} weight="bold" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-zinc-900 border-white/10">
                          {m.role === "member" && (
                            <DropdownMenuItem data-testid={`assign-machines-${m.user_id}`} onClick={() => openAssign(m)} className="cursor-pointer">
                              <Desktop size={15} className="mr-2" /> Assign machines
                            </DropdownMenuItem>
                          )}
                          {m.role === "member" ? (
                            <DropdownMenuItem onClick={() => changeRole(m, "admin")} className="cursor-pointer">
                              <ShieldCheck size={15} className="mr-2" /> Make admin
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => changeRole(m, "member")} className="cursor-pointer">
                              <UserIcon size={15} className="mr-2" /> Make member
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem data-testid={`remove-member-${m.user_id}`} onClick={() => setRemoveTarget(m)} className="cursor-pointer text-red-400 focus:text-red-400 focus:bg-red-500/10">
                            <Trash size={15} className="mr-2" /> Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              );
            })}

            {data.invites.map((inv) => (
              <div key={inv.invite_id} data-testid={`invite-row-${inv.invite_id}`} className="flex items-center justify-between px-5 py-4 border-b border-white/10 last:border-0 bg-zinc-900/30">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center">
                    <Clock size={16} className="text-zinc-500" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{inv.email}</div>
                    <div className="font-mono text-xs text-zinc-500">Pending invite · {inv.role}</div>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-3">
                    <button
                      data-testid={`resend-invite-${inv.invite_id}`}
                      onClick={() => resendInvite(inv)}
                      disabled={resending === inv.invite_id}
                      className="text-xs text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50"
                    >
                      {resending === inv.invite_id ? "Sending…" : "Resend"}
                    </button>
                    <button data-testid={`revoke-invite-${inv.invite_id}`} onClick={() => revokeInvite(inv)} className="text-xs text-red-400 hover:text-red-300 transition-colors">
                      Revoke
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-zinc-900 border-white/10" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight">Invite a member</DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              They'll join this team when they sign in with this email via Google.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div>
              <Label className="text-sm text-zinc-300">Email</Label>
              <Input data-testid="invite-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" className="mt-2 bg-black border-zinc-800 focus-visible:ring-blue-600" autoFocus />
            </div>
            <div>
              <Label className="text-sm text-zinc-300">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="invite-role-select" className="mt-2 bg-black border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value="member">Member: access only assigned machines</SelectItem>
                  <SelectItem value="admin">Admin: manage machines &amp; members</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInviteOpen(false)} className="text-zinc-400">Cancel</Button>
            <Button data-testid="send-invite-button" onClick={sendInvite} disabled={!email.trim()} className="bg-blue-600 hover:bg-blue-500">Send invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign machines dialog */}
      <Dialog open={!!assignTarget} onOpenChange={(v) => !v && setAssignTarget(null)}>
        <DialogContent className="bg-zinc-900 border-white/10" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight">Assign machines</DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              Choose which machines {assignTarget?.name || "this member"} can access.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 max-h-72 overflow-y-auto space-y-1">
            {machines.length === 0 ? (
              <p className="text-sm text-zinc-500">No machines in this team yet.</p>
            ) : machines.map((mac) => (
              <label key={mac.machine_id} className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-zinc-800/60 cursor-pointer">
                <Checkbox
                  data-testid={`assign-checkbox-${mac.machine_id}`}
                  checked={assignIds.includes(mac.machine_id)}
                  onCheckedChange={(c) => setAssignIds((prev) => c ? [...prev, mac.machine_id] : prev.filter((x) => x !== mac.machine_id))}
                />
                <span className="text-sm">{mac.name}</span>
                <span className="font-mono text-xs text-zinc-500 ml-auto">{mac.os || "unpaired"}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignTarget(null)} className="text-zinc-400">Cancel</Button>
            <Button data-testid="save-assign-button" onClick={saveAssign} className="bg-blue-600 hover:bg-blue-500">Save access</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!removeTarget} onOpenChange={(v) => !v && setRemoveTarget(null)}>
        <AlertDialogContent className="bg-zinc-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Remove member?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {removeTarget?.name} will lose access to this team and its machines.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 hover:bg-zinc-800">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove} className="bg-red-600 hover:bg-red-500" data-testid="confirm-remove-member">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
